import type { HubDataset } from '@robovision/hub'
import {
  HubAnnotationStatus,
  HubSplitStrategy,
} from '@robovision/hub'
import type { ClassDistribution } from '@robovision/rvai-base'
import {
  addOrReplaceBy,
  distinctByKey,
  HttpStatusCode,
  mergeArraysByKey,
  naturalSort,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  toRefs,
} from 'vue'
import { CredibilityScoreThreshold } from '@robovision/rvai-base'
import {
  CreateDatasetParams,
  CreateSplitParams,
} from '~domain/dataset/CreateDataset'
import { Dataset } from '~domain/dataset/Dataset'
import { DatasetMetadata } from '~domain/dataset/DatasetMetadata'
import {
  dataApi,
  hubApi,
} from '~services/api'
import { queryClient } from '~services/query-client'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'
import { getDatasetSplitName } from '~utils/dataset'
import { filtersToQuery } from '~utils/filters'


export const useDatasetsStore = defineStore('datasets', () => {
  const state = reactive({
    classDistributions: new Map<string, ClassDistribution>(),
    // Contains datasets that are dependencies or artifacts of jobs.
    datasets: [] as Dataset[],
    metadata: new Map<string, DatasetMetadata>(),
    // We have to separate internal datasets from regular ones,
    // and we want to avoid leaking that concept to the API and FE
    // because these may change.
    // So, projectDatasets contains only datasets visible to the user (internal: false),
    // which are returned from the GET:/datasets endpoint.
    projectDatasets: new Map<string, Dataset[]>(),
    thumbnailUrls: new Map<string, string>(),
  })

  const getters = {
    getDataset (id: string): Dataset | undefined {
      return state.datasets.find(d => d.id === id)
    },
    getDatasets (filters: {
      exampleDefinitionId?: string
      projectId?: string
    }): Dataset[] {
      return state.datasets.filter(
        d =>
          (!filters.projectId || d.projectId === filters.projectId) &&
          (!filters.exampleDefinitionId || d.exampleDefinitionId === filters.exampleDefinitionId),
      )
    },
    getMetadata (id: string): DatasetMetadata | undefined {
      return state.metadata.get(id)
    },
    getProjectDatasets (projectId?: string, exampleDefinitionId?: string): Dataset[] {
      const datasets = projectId
        ? state.projectDatasets.get(projectId) ?? []
        : distinctByKey([...state.projectDatasets.values()].flat(), 'id')

      return exampleDefinitionId
        ? datasets.filter(d => d.exampleDefinitionId === exampleDefinitionId)
        : datasets
    },
    getThumbnailUrl (id: string): string | undefined {
      return state.thumbnailUrls.get(id)
    },
  }

  const actions = {
    async createDataset (
      {
        annotationSource,
        datapoolId,
        examplePickingStrategy,
        filters,
        internal,
        minimumAnnotationCredibility = CredibilityScoreThreshold.DatasetCreation,
        name,
      }: CreateDatasetParams,
    ): Promise<Dataset> {
      const { data } = await hubApi.datapools.createDataset(datapoolId, {
        exampleFilters: {
          annotationSourceIds: annotationSource ? [[annotationSource]] : undefined,
        },
        examplePickingStrategy,
        internal,
        minimumAnnotationCredibility,
        name,
        sampleFilters: {
          ...filtersToQuery(filters),
          annotationStatuses: [HubAnnotationStatus.COMPLETE, HubAnnotationStatus.PARTIAL],
        },
      })

      const dataset = Dataset.fromResponse(data)

      if (internal) {
        state.datasets = addOrReplaceBy(
          state.datasets,
          dataset,
          d => d.id === dataset.id,
        )
      } else {
        const existedDatasetsForProject = state.projectDatasets.get(dataset.projectId)
        state.projectDatasets.set(
          dataset.projectId,
          existedDatasetsForProject
            ? distinctByKey([dataset, ...existedDatasetsForProject], 'id')
              .sort((a, b) => naturalSort(b.createdAt.toISOString(), a.createdAt.toISOString()))
            : [dataset],
        )
      }

      return dataset
    },

    async createSplit ({
      annotationSource,
      datapoolId,
      examplePickingStrategy,
      filters,
      name,
      split,
    }: CreateSplitParams): Promise<Dataset[]> {
      const { data } = await hubApi.datapools.createSplit(
        datapoolId,
        {
          exampleFilters: {
            annotationSourceIds: annotationSource ? [[annotationSource]] : undefined,
          },
          examplePickingStrategy,
          minimumAnnotationCredibility: CredibilityScoreThreshold.DatasetCreation,
          parts: [
            {
              description: `Split ${split}%, Strategy: ${examplePickingStrategy.toString()}}`,
              name: getDatasetSplitName(name, 'a'),
              ratio: (split / 100),
            },
            {
              description: `Split ${100 - split}%, Strategy: ${examplePickingStrategy.toString()}}`,
              name: getDatasetSplitName(name, 'b'),
              ratio: 1 - (split / 100),
              testSet: true,
            },
          ],
          sampleFilters: filtersToQuery(filters),
          splitStrategy: HubSplitStrategy.STRATIFIED,
        },
      )

      const datasets = data.map(Dataset.fromResponse)

      const projectId = datasets[0]?.projectId
      if (projectId) {
        const existedDatasetsForProject = state.projectDatasets.get(projectId)
        state.projectDatasets.set(
          projectId,
          existedDatasetsForProject
            ? distinctByKey([...datasets, ...existedDatasetsForProject], 'id')
              .sort((a, b) => naturalSort(b.createdAt.toISOString(), a.createdAt.toISOString()))
            : datasets,
        )
      }

      return datasets
    },

    async deleteProjectDataset (id: string): Promise<void> {
      try {
        await hubApi.datasets.deleteDataset(id)
        // delete dataset with id from state.datasets
        state.datasets = state.datasets.filter(d => d.id !== id)
        state.metadata.delete(id)

        const projectId = getters.getProjectDatasets().find(d => d.id === id)?.projectId
        if (projectId) {
          const datasets = state.projectDatasets.get(projectId)?.filter(d => d.id !== id) ?? []
          state.projectDatasets.set(projectId, datasets)
        }
      } catch (e) {
        console.error('Delete dataset', e)
      }
    },

    async loadClassDistribution (datasetId: string): Promise<void> {
      // always ignore already loaded classes - as datasets are immutable
      if (state.classDistributions.has(datasetId)) return

      const [
        { data: { count: annotatedCount } },
        { data: { count: emptyAnnotationCount } },
        { data: { nodeKeys } },
      ] = await Promise.all([
        // count the total number of examples
        dataApi.sets.countSetExamples(datasetId),
        // count empty annotations examples
        dataApi.sets.countSetExamples(datasetId, {
          annotationCounts: [['0']],
        }),
        // count the number of samples per class
        dataApi.sets.countSetClasses(datasetId),
      ])

      const classDistribution: ClassDistribution = {
        entries: [
          ...(emptyAnnotationCount ? [{
            count: emptyAnnotationCount,
            id: '',
          }] : []),
          ...nodeKeys.flatMap(node => node.classes.map(c => ({
            count: c.sampleCount,
            id: c.id,
          }))),
        ],
        total: annotatedCount,
      }

      state.classDistributions.set(datasetId, classDistribution)
    },

    async loadDataset (
      datasetId: string,
      {
        allowNotFound = true,
        loadMetadata = false,
      }: {
        allowNotFound?: boolean
        loadMetadata?: boolean
      } = {},
    ): Promise<Dataset | undefined> {
      try {
        const {
          data: hubDataset,
          status,
        } = await queryClient.fetchQuery({
          queryFn: () => hubApi.datasets.getDataset(
            datasetId,
            {},
            allowNotFound
              ? {
                ...RETHROW_ERROR_AFTER_HANDLING,
                validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
              }
              : RETHROW_ERROR_AFTER_HANDLING,
          ),
          queryKey: ['get-dataset', datasetId, allowNotFound],
        })

        // If dataset not found - remove it from state
        if (status === HttpStatusCode.NOT_FOUND) {
          state.datasets = state.datasets.filter(d => d.id !== datasetId)
          state.metadata.delete(datasetId)
          return
        }

        if (loadMetadata) {
          await this.loadMetadata(datasetId)
        }

        const dataset = Dataset.fromResponse(hubDataset)
        state.datasets = addOrReplaceBy(
          state.datasets,
          dataset,
          d => d.id === dataset.id,
        )

        return dataset
      } catch (e) {
        console.error('Load dataset', e)
      }
    },

    async loadMetadata (
      datasetId: string,
      cached = true,
      allowNotFound: boolean = true,
    ): Promise<void> {
      if (cached) {
        const response = await queryClient.fetchQuery({
          queryFn: () => dataApi.sets.getCachedSetMetadata(
            datasetId,
            allowNotFound
              ? {
                ...RETHROW_ERROR_AFTER_HANDLING,
                validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
              }
              : RETHROW_ERROR_AFTER_HANDLING,
          ),
          queryKey: ['get-cached-set-metadata', datasetId, allowNotFound],
        })
        const {
          data: poolMetadata,
          status,
        } = response

        // this assumes a dataset with no samples indicates that the
        // cache is not yet ready. so we'll trigger a normal request
        if (status !== HttpStatusCode.NOT_FOUND && poolMetadata.sampleCount) {
          const data = DatasetMetadata.fromResponse(poolMetadata)
          state.metadata.set(datasetId, data)
          return
        }
      }

      const {
        data: poolMetadata,
        status,
      } = await dataApi.sets.getSetMetadata(
        datasetId,
        allowNotFound
          ? {
            validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
          }
          : {},
      )

      // If metadata not found - remove it from state
      if (status === HttpStatusCode.NOT_FOUND) {
        state.metadata.delete(datasetId)
        return
      }

      const data = DatasetMetadata.fromResponse(poolMetadata)
      state.metadata.set(datasetId, data)
    },

    // TODO the implementation is similar to a dragon, should be improved in https://robovision.atlassian.net/browse/PD-11621
    async loadProjectDatasets ({
      datapoolId,
      exampleDefinitionId,
      options: {
        loadMetadata, // Use with care!
        loadMetadataLimit,
      } = {},
      projectId,
    }: {
      datapoolId?: string
      exampleDefinitionId?: string
      options?: {
        loadMetadata?: boolean // Use with care!
        loadMetadataLimit?: number
      }
      projectId?: string
    } = {}): Promise<Dataset[]> {
      const { data } = await hubApi.datasets.listDatasets({
        datapoolId,
        exampleDefinitionId,
        projectId,
      })
      const datasets = data.items.map(Dataset.fromResponse)
      const slicedDatasets = loadMetadataLimit !== undefined
        ? datasets.slice(0, loadMetadataLimit)
        : datasets

      await Promise.all(loadMetadata ? slicedDatasets.map(d => this.loadMetadata(d.id)) : [])

      const loadedDatasetIds = datasets.map(d => d.id)

      if (!datasets.length && projectId) {
        state.projectDatasets.set(projectId, [])
      } else {
        for (const dataset of datasets) {
          const existingDatasets = state.projectDatasets
            .get(dataset.projectId)
            ?.filter(d => loadedDatasetIds.includes(d.id)) ?? [] // remove deleted datasets from the store

          state.projectDatasets.set(
            dataset.projectId,
            addOrReplaceBy(
              existingDatasets,
              dataset,
              d => d.id === dataset.id,
            ).sort((a, b) => naturalSort(b.createdAt.toISOString(), a.createdAt.toISOString())),
          )
        }
      }


      return datasets
    },

    async loadThumbnailUrl (datasetId: string): Promise<void> {
      const dataset = state.datasets.find(d => d.id === datasetId)
      if (!dataset) return

      const limit = 1
      const examplesResponse = await queryClient.fetchQuery({
        queryFn: () => dataApi.sets.listSetExamples(
          dataset.id,
          {
            limit,
          },
          {
            ...RETHROW_ERROR_AFTER_HANDLING,
            validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
          },
        ),
        queryKey: ['list-set-examples', dataset.id, limit],
      })
      const examples = examplesResponse && examplesResponse?.data?.examples
      if (!examples?.length) return

      const [example] = examples
      const thumbnailUrl = example ? `/datastore${example.thumbnail}` : ''
      state.thumbnailUrls.set(datasetId, thumbnailUrl)
    },

    setDatasets (mappedDatasets: HubDataset[]) {
      const datasets = mappedDatasets.map(Dataset.fromResponse)
      state.datasets = mergeArraysByKey(state.datasets, datasets, 'id')
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async updateProjectDataset (dataset: Dataset): Promise<void> {
      try {
        const { data } = await hubApi.datasets.updateDataset(dataset.id, {
          name: dataset.name,
        })

        const updatedDataset = Dataset.fromResponse(data)

        const existedDatasetsForProject = state.projectDatasets.get(updatedDataset.projectId)
        state.projectDatasets.set(
          updatedDataset.projectId,
          distinctByKey([updatedDataset, ...existedDatasetsForProject as Dataset[]], 'id')
            .sort((a, b) => naturalSort(b.createdAt.toISOString(), a.createdAt.toISOString())),
        )
      } catch (e) {
        console.error('Update dataset', e)
      }
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
